import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { ExternalLink, CheckCircle, XCircle, AlertCircle } from 'lucide-react';

const PageContainer = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding: 40px 20px;
`;

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #333;
  margin-bottom: 8px;
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #666;
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  padding: 32px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 24px;
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #555;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const Button = styled.button`
  padding: 14px 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: transform 0.2s;
  
  &:hover {
    transform: translateY(-2px);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const ResultBox = styled.div`
  padding: 16px;
  border-radius: 8px;
  margin-top: 24px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  
  &.success {
    background: #d4edda;
    border: 1px solid #c3e6cb;
    color: #155724;
  }
  
  &.error {
    background: #f8d7da;
    border: 1px solid #f5c6cb;
    color: #721c24;
  }
  
  &.info {
    background: #d1ecf1;
    border: 1px solid #bee5eb;
    color: #0c5460;
  }
`;

const ResultText = styled.div`
  flex: 1;
  
  h3 {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 8px;
  }
  
  p {
    font-size: 14px;
    margin-bottom: 4px;
  }
  
  code {
    background: rgba(0, 0, 0, 0.1);
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 12px;
  }
`;

const InfoBox = styled.div`
  background: #e7f3ff;
  border: 1px solid #b3d9ff;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
  font-size: 14px;
  color: #004085;
  
  strong {
    display: block;
    margin-bottom: 8px;
  }
  
  ul {
    margin: 8px 0 0 20px;
  }
  
  li {
    margin-bottom: 4px;
  }
`;

const RestoPaymentTest = () => {
  const [formData, setFormData] = useState({
    amount: '1000',
    paymentType: 'subscription',
    planName: 'Premium Plan',
    restaurantName: 'Test Restaurant',
    customerName: 'John Doe',
    customerPhone: '9876543210',
    customerEmail: 'john@example.com',
    orderId: '',
  });
  
  const [result, setResult] = useState(null);
  const [popup, setPopup] = useState(null);

  useEffect(() => {
    // Listen for postMessage from popup
    const handleMessage = (event) => {
      // In production, verify origin
      // if (event.origin !== 'https://willovate.in') return;
      
      if (event.data.type === 'PAYMENT_RESULT') {
        console.log('Payment result received:', event.data);
        setResult(event.data);
        
        // Close popup if still open
        if (popup && !popup.closed) {
          popup.close();
        }
      }
    };

    window.addEventListener('message', handleMessage);
    
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [popup]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleOpenPayment = () => {
    setResult(null);
    
    // Build payment URL
    const params = new URLSearchParams({
      amount: formData.amount,
      paymentType: formData.paymentType,
      origin: encodeURIComponent(window.location.origin),
      mode: 'popup',
    });
    
    // Add optional parameters
    if (formData.planName) params.append('planName', formData.planName);
    if (formData.restaurantName) params.append('restaurantName', formData.restaurantName);
    if (formData.customerName) params.append('customerName', formData.customerName);
    if (formData.customerPhone) params.append('customerPhone', formData.customerPhone);
    if (formData.customerEmail) params.append('customerEmail', formData.customerEmail);
    if (formData.orderId) params.append('orderId', formData.orderId);
    
    const paymentUrl = `/payment/resto?${params.toString()}`;
    
    console.log('Opening payment popup:', paymentUrl);
    
    // Open popup window
    const newPopup = window.open(
      paymentUrl,
      'PhonePe Payment',
      'width=500,height=700,scrollbars=yes,resizable=yes'
    );
    
    if (!newPopup) {
      setResult({
        type: 'PAYMENT_RESULT',
        status: 'ERROR',
        message: 'Popup blocked by browser. Please allow popups for this site.',
      });
    } else {
      setPopup(newPopup);
    }
  };

  return (
    <PageContainer>
      <Container>
        <Header>
          <Title>PhonePe Resto Payment Test</Title>
          <Subtitle>Test payment integration with popup window</Subtitle>
        </Header>

        <InfoBox>
          <strong>How it works:</strong>
          <ul>
            <li>Fill in payment details below</li>
            <li>Click "Open Payment Popup" to launch payment window</li>
            <li>In the popup, use "Test Success" or "Test Failure" buttons</li>
            <li>Payment result will appear below after popup closes</li>
          </ul>
        </InfoBox>

        <Card>
          <SectionTitle>Payment Details</SectionTitle>
          
          <FormGroup>
            <Label>Amount (₹)</Label>
            <Input
              type="number"
              name="amount"
              value={formData.amount}
              onChange={handleChange}
              placeholder="1000"
            />
          </FormGroup>

          <FormGroup>
            <Label>Payment Type</Label>
            <Select
              name="paymentType"
              value={formData.paymentType}
              onChange={handleChange}
            >
              <option value="subscription">Subscription</option>
              <option value="order">Order</option>
              <option value="test">Test</option>
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>Plan Name (optional)</Label>
            <Input
              type="text"
              name="planName"
              value={formData.planName}
              onChange={handleChange}
              placeholder="Premium Plan"
            />
          </FormGroup>

          <FormGroup>
            <Label>Restaurant Name (optional)</Label>
            <Input
              type="text"
              name="restaurantName"
              value={formData.restaurantName}
              onChange={handleChange}
              placeholder="Test Restaurant"
            />
          </FormGroup>

          <FormGroup>
            <Label>Customer Name (optional)</Label>
            <Input
              type="text"
              name="customerName"
              value={formData.customerName}
              onChange={handleChange}
              placeholder="John Doe"
            />
          </FormGroup>

          <FormGroup>
            <Label>Customer Phone (optional)</Label>
            <Input
              type="tel"
              name="customerPhone"
              value={formData.customerPhone}
              onChange={handleChange}
              placeholder="9876543210"
            />
          </FormGroup>

          <FormGroup>
            <Label>Customer Email (optional)</Label>
            <Input
              type="email"
              name="customerEmail"
              value={formData.customerEmail}
              onChange={handleChange}
              placeholder="john@example.com"
            />
          </FormGroup>

          <FormGroup>
            <Label>Order ID (optional, for order payments)</Label>
            <Input
              type="text"
              name="orderId"
              value={formData.orderId}
              onChange={handleChange}
              placeholder="ORDER_123"
            />
          </FormGroup>

          <Button onClick={handleOpenPayment}>
            <ExternalLink size={20} />
            Open Payment Popup
          </Button>

          {result && (
            <ResultBox className={
              result.status === 'SUCCESS' ? 'success' :
              result.status === 'FAILED' ? 'error' :
              result.status === 'CANCELLED' ? 'info' :
              'error'
            }>
              {result.status === 'SUCCESS' ? <CheckCircle size={24} /> :
               result.status === 'FAILED' ? <XCircle size={24} /> :
               <AlertCircle size={24} />}
              
              <ResultText>
                <h3>
                  {result.status === 'SUCCESS' ? 'Payment Successful!' :
                   result.status === 'FAILED' ? 'Payment Failed' :
                   result.status === 'CANCELLED' ? 'Payment Cancelled' :
                   'Payment Error'}
                </h3>
                <p><strong>Message:</strong> {result.message}</p>
                {result.transactionId && (
                  <p><strong>Transaction ID:</strong> <code>{result.transactionId}</code></p>
                )}
                {result.merchantTransactionId && (
                  <p><strong>Merchant Txn ID:</strong> <code>{result.merchantTransactionId}</code></p>
                )}
                {result.amount && (
                  <p><strong>Amount:</strong> ₹{result.amount}</p>
                )}
              </ResultText>
            </ResultBox>
          )}
        </Card>

        <Card>
          <SectionTitle>Integration Info</SectionTitle>
          <p style={{ fontSize: '14px', color: '#666', lineHeight: '1.6' }}>
            This test page simulates how willovate-resto-ui will integrate payment.
            The payment opens in a popup window, and results are sent back via postMessage API.
          </p>
          <p style={{ fontSize: '14px', color: '#666', lineHeight: '1.6', marginTop: '12px' }}>
            <strong>Note:</strong> In development mode, the payment page includes "Test Success" 
            and "Test Failure" buttons to simulate payment without PhonePe integration.
          </p>
        </Card>
      </Container>
    </PageContainer>
  );
};

export default RestoPaymentTest;
